import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EventData } from "@/contexts/event/EventContext";
import { useSupabase } from "@/contexts/supabase";
import { toast } from "sonner";
import { useLanguage } from "@/hooks/use-language";
import { addDays, format } from "date-fns";

interface DuplicateEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: EventData | null;
  onDuplicated: () => void;
}

export function DuplicateEventDialog({ open, onOpenChange, event, onDuplicated }: DuplicateEventDialogProps) {
  const supabase = useSupabase();
  const { t } = useLanguage();
  const [duplicateType, setDuplicateType] = useState<'single' | 'recurring'>('single');
  const [newDate, setNewDate] = useState(() => event ? format(addDays(event.date, 7), 'yyyy-MM-dd') : '');
  const [newTime, setNewTime] = useState(event?.time || '');
  const [recurrencePattern, setRecurrencePattern] = useState<'daily' | 'weekly' | 'biweekly' | 'monthly'>('weekly');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDuplicate = async () => {
    if (!event || !newDate || !newTime) {
      toast.error(t('fillAllFields'));
      return;
    }

    setLoading(true);
    try {
      if (duplicateType === 'single') {
        // Duplicate as single event
        const { error } = await (supabase as any).functions.invoke('manage-event', {
          body: {
            action: 'duplicate_event',
            eventId: event.id,
            newDate,
            newTime
          }
        });

        if (error) throw error;
        toast.success('Event duplicated successfully');
      } else {
        // Duplicate as recurring series
        if (!recurrenceEndDate) {
          toast.error('Please set an end date for the recurring series');
          return;
        }

        const { error } = await (supabase as any).functions.invoke('manage-event', {
          body: {
            action: 'duplicate_as_recurring',
            eventId: event.id,
            newDate,
            newTime,
            recurrencePattern,
            recurrenceEndDate
          }
        });

        if (error) throw error;
        toast.success('Recurring series created successfully');
      }

      onDuplicated();
      onOpenChange(false);
    } catch (error) {
      console.error('Duplicate error:', error);
      toast.error('Failed to duplicate event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Duplicate Event</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Duplicate Type</Label>
            <Select value={duplicateType} onValueChange={(v: 'single' | 'recurring') => setDuplicateType(v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single">Single Event</SelectItem>
                <SelectItem value="recurring">Recurring Series</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>New Date</Label>
            <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
          </div>

          <div>
            <Label>New Time</Label>
            <Input type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)} />
          </div>

          {duplicateType === 'recurring' && (
            <>
              <div>
                <Label>Recurrence Pattern</Label>
                <Select value={recurrencePattern} onValueChange={(v: any) => setRecurrencePattern(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="biweekly">Bi-weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>End Date</Label>
                <Input type="date" value={recurrenceEndDate} onChange={(e) => setRecurrenceEndDate(e.target.value)} />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleDuplicate} disabled={loading}>
            {loading ? 'Duplicating...' : 'Duplicate Event'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
